import { isWithinInterval } from 'date-fns';
import { DutyHourType } from 'generated';
import { round } from 'lodash';

import {
  AdminSettings,
  PlannedDutyPeriod,
  PlanUnitMix,
  Setting,
  WorkAgreementDetails
} from './types';

import { fromISOString } from 'utils/date';
import dayjs from 'dayjs';

// 1920: do not increase visual elements for screens larger than full HD
export const rSize = (size: number) =>
  `clamp(${round(size * 0.7, 3)}px, ${round(
    (size * 100) / 1920,
    3
  )}vw, ${size}px)`;

// 1920: do not increase visual elements for screens larger than full HD
export const rSizeN = (size: number) => `calc(${rSize(size)} * -1)`;

export const getPlanUnitIds = (planUnits: PlanUnitMix[]) =>
  planUnits
    .map((planUnit) =>
      'costCenters' in planUnit
        ? planUnit.costCenters.map(({ id }) => id)
        : [planUnit.id]
    )
    .flat();

export const getPlanunitCommaIds = (planUnits: PlanUnitMix[]) =>
  getPlanUnitIds(planUnits).join(',');

export const getDefaultDHT = (dhts: DutyHourType[] | undefined) =>
  dhts?.find(
    ({ dutyHourTypeFlags }) =>
      dutyHourTypeFlags && dutyHourTypeFlags.includes('DEFAULT')
  );

export const sortPeriods = (periods: PlannedDutyPeriod[]) =>
  periods.sort((p1, p2) =>
    new Date(p1.startTime) > new Date(p2.startTime)
      ? 1
      : new Date(p1.startTime) < new Date(p2.startTime)
      ? -1
      : 0
  );

export const getReferenceDate = (
  startDate: Date | null,
  endDate: Date | null
) => {
  const today = new Date();
  return startDate == null ||
    (startDate <= today && (endDate == null || endDate > today))
    ? today
    : startDate;
};

export const getReferenceBenefit = (
  workAgreement: WorkAgreementDetails,
  startDate: Date | null,
  endDate: Date | null
) =>
  workAgreement.benefits.find(
    (b) =>
      isWithinInterval(getReferenceDate(startDate, endDate), {
        start: fromISOString(b.validFrom),
        end: fromISOString(b.validTo)
      }) && !b.flags.includes('INVALID')
  );

export const getReferenceBenefitOrDefault = (
  workAgreement: WorkAgreementDetails,
  startDate: Date | null,
  endDate: Date | null
) => {
  const benefit = getReferenceBenefit(workAgreement, startDate, endDate);
  return benefit != null
    ? benefit
    : workAgreement.benefits.find((b) => !b.flags.includes('INVALID'));
};

export const transformAdminSettingsToFormValues = (
  data: AdminSettings | undefined
): Record<string, any> | undefined => {
  if (!data?.settings.length) return;

  return data.settings.reduce((settingsObj, setting) => {
    const key = setting.key.replaceAll('.', '_');
    settingsObj[key] =
      key === 'neg_hours_factor'
        ? parseFloat(setting.value) * 100
        : key === 'use_distance_webservice' ||
          key === 'include_business_travel_movements'
        ? setting.value === 'true'
        : key === 'travel_movements_activation_date'
        ? setting.value
          ? dayjs(setting.value).startOf('day').toDate()
          : ''
        : setting.value;
    return settingsObj;
  }, {});
};

export const transformAdminSettingsToRequestValues = (
  data: Record<string, any>,
  settings: Setting[] | undefined
): Setting[] | undefined => {
  return settings?.map((item) => {
    const key = item.key.replaceAll('.', '_');
    const value =
      key === 'neg_hours_factor'
        ? String(parseFloat(data[key]) / 100)
        : key === 'use_distance_webservice' ||
          key === 'include_business_travel_movements'
        ? String(data[key])
        : key === 'travel_movements_activation_date'
        ? data[key]
          ? dayjs(data[key]).startOf('day').format('YYYY-MM-DD')
          : ''
        : data[key];
    return { ...item, value };
  });
};
